/** Sidebar: brand row, search, grouped navigation with collapsible trees, a pinned bottom group and the user card. */
import { createContext, useContext, useEffect, useRef, type ReactNode } from "react";
import { useSidebarSlot } from "./AppShell";
import { cx, focusRing } from "./cx";
import { Graphic, Icon, type IconName } from "./icons";
import { SearchInput, SearchInputHint } from "./SearchInput";
import { Tooltip } from "./Tooltip";

/** Fill an absolutely placed box: the artwork bleeds past its layout box by the insets given. */
const BLEED = "block size-full max-w-none";

export function Sidebar({ children }: { children: ReactNode }) {
  const { className } = useSidebarSlot();
  return (
    <aside className={cx("flex h-full w-sidebar shrink-0 flex-col", className)} aria-label="Primary">
      {children}
    </aside>
  );
}

interface SidebarBrandProps {
  name: string;
  /** The mark, drawn to fill a 24 px box (it may bleed past it). */
  logo: ReactNode;
  href?: string;
}

/** The brand link and the button that hides the sidebar. */
export function SidebarBrand({ name, logo, href = "#" }: SidebarBrandProps) {
  const { dismiss } = useSidebarSlot();
  return (
    <div className="flex w-sidebar items-center justify-between gap-3 overflow-clip px-3 py-3.5">
      <a href={href} className={cx("flex min-w-0 flex-1 items-center gap-3 rounded-md", focusRing)}>
        <span className="relative size-6 shrink-0 transition-transform duration-200 ease-out-smooth hover:rotate-[-8deg] hover:scale-105">{logo}</span>
        <span className="flex-1 text-heading font-medium leading-none text-foreground">{name}</span>
      </a>
      <Tooltip content="Collapse sidebar" side="right">
        <button type="button" onClick={dismiss} aria-label="Collapse sidebar" className={cx("-m-2 flex size-8 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-black/[0.05]", focusRing)}>
          <Icon name="sidebar" />
        </button>
      </Tooltip>
    </div>
  );
}

/** Everything under the brand row. Starts with the dashed rule. */
export function SidebarBody({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-0 w-sidebar flex-1 flex-col gap-5 px-3 pb-4">
      <div className="relative h-0 w-full shrink-0">
        <span className="pointer-events-none absolute block" style={{ inset: "-0.5px -0.22%" }}>
          <Graphic name="rule-sidebar" className={BLEED} />
        </span>
      </div>
      {children}
    </div>
  );
}

/** The search field; ⌘K or Ctrl K focuses it from anywhere. */
export function SidebarSearch({ label }: { label: string }) {
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        input.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return (
    <SearchInput
      ref={input}
      label={label}
      raised
      className="w-full shrink-0"
      trailing={
        <SearchInputHint>
          <span className="flex size-4 items-center justify-center rounded p-0.5">
            <Icon name="command" size={12} />
          </span>
          <span className="flex size-4 items-center justify-center rounded p-0.5 text-caption font-medium leading-none text-kbd-foreground">K</span>
        </SearchInputHint>
      }
    />
  );
}

/** The scrolling navigation. `footer` is pinned to the bottom. */
export function SidebarNav({ children, footer }: { children: ReactNode; footer?: ReactNode }) {
  return (
    <nav className="-mx-[3px] flex min-h-0 w-[calc(100%+6px)] flex-1 flex-col items-center justify-between gap-6 overflow-y-auto overflow-x-hidden px-[3px] pb-[3px] [scrollbar-width:none]">
      <div className="flex w-full flex-col gap-6">{children}</div>
      {footer}
    </nav>
  );
}

/** A labelled group of items. `tight` drops the gap between them (the bottom group). */
export function SidebarGroup({ label, tight = false, children }: { label: string; tight?: boolean; children: ReactNode }) {
  return (
    <div className="flex w-full flex-col gap-2">
      <p className="whitespace-nowrap text-caption font-medium uppercase tracking-[0.04em] text-muted-foreground">{label}</p>
      <div className={cx("flex w-full flex-col", !tight && "gap-0.5")}>{children}</div>
    </div>
  );
}

/** True while the enclosing tree is open, so closed sub-items leave the tab order. */
const TreeOpen = createContext(false);

interface SidebarItemProps {
  label: string;
  icon: IconName;
  /** The current page: the white pill. */
  active?: boolean;
  /** Fainter ink, for secondary groups. */
  muted?: boolean;
  /** A 40 px row with a tighter icon gap. */
  tall?: boolean;
  onSelect?: () => void;
  /** With `children`, the row is a tree: `open` and `onToggle` drive it. */
  open?: boolean;
  onToggle?: () => void;
  /** SidebarSubItems. */
  children?: ReactNode;
}

export function SidebarItem({ label, icon, active = false, muted = false, tall = false, onSelect, open = false, onToggle, children }: SidebarItemProps) {
  const tree = children !== undefined;
  return (
    <div className="flex w-full flex-col">
      <button
        type="button"
        onClick={tree ? onToggle : onSelect}
        aria-expanded={tree ? open : undefined}
        aria-current={active ? "page" : undefined}
        className={cx(
          "group/nav flex w-full items-center justify-between gap-2 rounded-lg border-hair px-2.5 py-[7px] text-left text-body leading-5 transition-[background-color,box-shadow,border-color,color] duration-150",
          focusRing,
          tall ? "min-h-10" : "min-h-9",
          active ? "border-border bg-card font-medium text-brand shadow-nav" : "border-transparent hover:bg-card/70 hover:text-foreground",
          !active && (muted ? "text-muted-foreground" : "text-secondary-foreground"),
        )}
      >
        <span className={cx("flex min-w-0 items-center", tall ? "gap-2" : "gap-2.5")}>
          {/* The active row's icon stands on a 24 px wash of the brand tint; the box keeps the icon where it was. */}
          <span className={cx("-m-[3px] flex size-6 shrink-0 items-center justify-center rounded-md transition-[transform,background-color] duration-150 ease-out group-hover/nav:scale-110", active && "bg-brand-tint")}>
            <Icon name={icon} size={18} brand={active} />
          </span>
          <span className="min-w-0">{label}</span>
        </span>
        {tree && (
          <span className={cx("flex transition-transform duration-200 ease-out-smooth", !open && "rotate-180")}>
            <Icon name="chevron-up" size={12} />
          </span>
        )}
      </button>
      {tree && (
        <div className={cx("grid transition-[grid-template-rows,opacity,margin] duration-200 ease-in-out", open ? "mt-0.5 grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0")}>
          <div className="overflow-hidden">
            <div className="flex flex-col gap-0.5">
              <TreeOpen.Provider value={open}>{children}</TreeOpen.Provider>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** A leaf of a tree, hung off the connector line. */
export function SidebarSubItem({ label, active = false, onSelect }: { label: string; active?: boolean; onSelect?: () => void }) {
  const open = useContext(TreeOpen);
  return (
    <button
      type="button"
      tabIndex={open ? 0 : -1}
      onClick={onSelect}
      aria-current={active ? "page" : undefined}
      className="group/sub relative flex h-8 w-full items-center justify-end pl-2.5 outline-none"
    >
      <span
        className={cx(
          "flex h-full w-[calc(100%-28px)] items-center rounded-md text-meta leading-none transition-[color,background-color,padding] duration-200 group-hover/sub:bg-card/70 group-hover/sub:pl-1.5 group-hover/sub:text-foreground group-focus-visible/sub:ring-[3px] group-focus-visible/sub:ring-ring",
          active ? "pl-1.5 font-medium text-brand" : "px-0 text-muted-foreground",
        )}
      >
        {label}
      </span>
      <span className="pointer-events-none absolute left-[18px] top-[-3px] h-5 w-2">
        <span className="pointer-events-none absolute block" style={{ inset: "-2% -5%" }}>
          <Graphic name="tree-line" className={BLEED} />
        </span>
      </span>
      <span className="pointer-events-none absolute left-[23px] top-[15px] size-1">
        <Graphic name="tree-dot" className={cx("absolute inset-0 block size-full transition-transform duration-200", active && "scale-150")} />
      </span>
    </button>
  );
}

interface SidebarUserProps {
  name: string;
  detail: string;
  initials: string;
  /** Shows the pulsing green dot. */
  online?: boolean;
  onClick?: () => void;
}

/** The account card at the foot of the sidebar. */
export function SidebarUser({ name, detail, initials, online = false, onClick }: SidebarUserProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        "group/acct flex w-full shrink-0 items-center gap-2 rounded-xl border-hair border-border bg-card py-2.5 pl-2.5 pr-3 text-left drop-shadow-card transition-[box-shadow,transform] duration-200 hover:shadow-lift active:scale-[0.99]",
        focusRing,
      )}
    >
      <span className="relative flex size-9 shrink-0 items-center justify-center rounded-full border-hair border-avatar-border bg-secondary p-2">
        <span className="whitespace-nowrap text-caption font-medium leading-none text-secondary-foreground">{initials}</span>
        {online && (
          <span className="absolute bottom-[-1.7px] right-[-1.7px] size-[9px]">
            <span className="absolute inset-0 animate-ping rounded-full bg-success/40 [animation-duration:2.4s]" />
            <Graphic name="online-dot" className="absolute inset-0 block size-full" />
          </span>
        )}
      </span>
      <span className="flex min-w-0 flex-1 flex-col justify-center gap-2 whitespace-nowrap leading-none">
        <span className="text-body font-medium text-foreground">{name}</span>
        <span className="text-meta break-all whitespace-normal text-muted-foreground">{detail}</span>
      </span>
      <span className="flex transition-transform duration-200 group-hover/acct:scale-110">
        <Icon name="selector" size={18} />
      </span>
    </button>
  );
}
